import React, { useState } from "react";
import { useScrollToTop } from "../../hooks/useScrollToTop";
import { Card, Checkbox, Typography, Row, Col, Button } from "antd";
import {
  ArrowRightOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useTender } from "../../contexts/TenderContext";
import { useDevMode } from "../../contexts/DevModeContext";
import { COLORS } from "../../utils/constants";
import { saveTenderToStorage } from "../../utils/storage";
import MainLayout from "../layout/MainLayout";
import { Layers3, Route } from "lucide-react";

const TenderJobTypes: React.FC = () => {
  useScrollToTop({ behavior: "smooth", delay: 100 });
  const { state, setJobTypes } = useTender();
  const devMode = useDevMode();
  const navigate = useNavigate();

  // A tender saved earlier (e.g. while dev mode was on) may carry both job
  // types - clamp to one on load for a non-dev user.
  const [localJobTypes, setLocalJobTypes] = useState(() =>
    !devMode && state.jobTypes.roadsAndSewers && state.jobTypes.groundworks
      ? { roadsAndSewers: true, groundworks: false }
      : {
          roadsAndSewers: state.jobTypes.roadsAndSewers,
          groundworks: state.jobTypes.groundworks,
        },
  );

  const isValid = localJobTypes.roadsAndSewers || localJobTypes.groundworks;

  // Non-dev users may only have one job type selected at a time - picking
  // one deselects the other, radio-button style. Dev mode keeps both
  // independently selectable.
  //
  // Persist the selection to TenderContext + localStorage immediately (not
  // just on Continue) - otherwise a page reload on this step remounts the
  // component, `localJobTypes` re-seeds from the last-persisted (still
  // unselected) state.jobTypes, and the Continue button drops back to
  // disabled even though the user had already picked a card.
  const handleJobTypeChange = (
    jobType: "roadsAndSewers" | "groundworks",
    checked: boolean,
  ) => {
    setLocalJobTypes((prev) => {
      const next = !devMode && checked
        ? {
            roadsAndSewers: jobType === "roadsAndSewers",
            groundworks: jobType === "groundworks",
          }
        : { ...prev, [jobType]: checked };
      setJobTypes(next.roadsAndSewers, next.groundworks);
      saveTenderToStorage({ ...state, jobTypes: next });
      return next;
    });
  };

  const handleContinue = () => {
    setJobTypes(localJobTypes.roadsAndSewers, localJobTypes.groundworks);
    navigate("/tender/import-opportunities");
  };

  return (
    <MainLayout canProceed={isValid} onNext={handleContinue} showFooter={false}>
      <div className="max-w-4xl mx-auto">
        <Card className="shadow-sm" bodyStyle={{ padding: 0 }}>
          {/* Header */}
          <div
            className="px-6 pt-6 pb-4"
            style={{ borderBottom: "1px solid #e5e7eb" }}
          >
            <Typography.Title
              level={3}
              className="mb-2"
              style={{ fontWeight: 600, fontSize: "20px", color: "#111827" }}
            >
              Select Job Types
            </Typography.Title>
            <Typography.Text style={{ color: "#9ca3af", fontSize: "14px" }}>
              Please select which types of construction work are included in
              this project.{" "}
              {devMode
                ? "You can select both job types."
                : "You can select one job type."}
            </Typography.Text>
          </div>

          <div className="px-6 py-6">
            <Row gutter={[24, 24]} className="mb-8">
              {/* Groundworks */}
              <Col xs={24} md={12}>
                <Card
                  onClick={() =>
                    handleJobTypeChange(
                      "groundworks",
                      !localJobTypes.groundworks,
                    )
                  }
                  style={{
                    backgroundColor: "#F3E8FF",
                    border: localJobTypes.groundworks
                      ? `2px solid ${COLORS.primary}`
                      : "1px solid #F3E8FF",
                    cursor: "pointer",
                    borderRadius: "8px",
                    height: "100%",
                  }}
                  bodyStyle={{ padding: "24px" }}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start gap-4">
                      <div
                        style={{
                          width: "56px",
                          height: "56px",
                          backgroundColor: "#fff",
                          borderRadius: "8px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Layers3 size={32} color="#9333EA" />
                      </div>
                      <div>
                        <Typography.Title
                          level={4}
                          style={{
                            margin: 0,
                            marginBottom: "4px",
                            fontWeight: 600,
                            fontSize: "18px",
                            color: "#111827",
                          }}
                        >
                          Groundworks
                        </Typography.Title>
                        <Typography.Text
                          style={{ color: "#6b7280", fontSize: "14px" }}
                        >
                          Building and plot-specific construction works:
                        </Typography.Text>
                      </div>
                    </div>
                    <Checkbox
                      checked={localJobTypes.groundworks}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) =>
                        handleJobTypeChange("groundworks", e.target.checked)
                      }
                    />
                  </div>

                  <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                    {[
                      "Building Foundations",
                      "External Works (Drives, Paths)",
                      "Garden & Landscaping",
                      "Private Drainage",
                      "Additional Items & Structures",
                    ].map((item) => (
                      <li
                        key={item}
                        style={{
                          color: "#374151",
                          fontSize: "14px",
                          marginBottom: "8px",
                          paddingLeft: "20px",
                          position: "relative",
                        }}
                      >
                        <span
                          style={{
                            position: "absolute",
                            left: 0,
                            top: "8px",
                            width: "4px",
                            height: "4px",
                            backgroundColor: "#374151",
                            borderRadius: "50%",
                          }}
                        ></span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </Card>
              </Col>

              {/* Roads & Sewers */}
              <Col xs={24} md={12}>
                <Card
                  onClick={() =>
                    handleJobTypeChange(
                      "roadsAndSewers",
                      !localJobTypes.roadsAndSewers,
                    )
                  }
                  style={{
                    backgroundColor: "#E8F4FD",
                    border: localJobTypes.roadsAndSewers
                      ? `2px solid ${COLORS.primary}`
                      : "1px solid #E8F4FD",
                    cursor: "pointer",
                    borderRadius: "8px",
                    height: "100%",
                  }}
                  bodyStyle={{ padding: "24px" }}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start gap-4">
                      <div
                        style={{
                          width: "56px",
                          height: "56px",
                          backgroundColor: "#fff",
                          borderRadius: "8px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Route size={32} color={COLORS.primary} />
                      </div>
                      <div>
                        <Typography.Title
                          level={4}
                          style={{
                            margin: 0,
                            marginBottom: "4px",
                            fontWeight: 600,
                            fontSize: "18px",
                            color: "#111827",
                          }}
                        >
                          Roads & Sewers
                        </Typography.Title>
                        <Typography.Text
                          style={{ color: "#6b7280", fontSize: "14px" }}
                        >
                          Infrastructure construction works:
                        </Typography.Text>
                      </div>
                    </div>
                    <Checkbox
                      checked={localJobTypes.roadsAndSewers}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) =>
                        handleJobTypeChange(
                          "roadsAndSewers",
                          e.target.checked,
                        )
                      }
                    />
                  </div>

                  <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                    {[
                      "Foul Sewers & Manholes",
                      "Storm Sewers & Drainage",
                      "Road Construction & Surfacing",
                      "Main Services (Water, Gas, Electric)",
                      "Preliminary Items & Site Setup",
                    ].map((item) => (
                      <li
                        key={item}
                        style={{
                          color: "#374151",
                          fontSize: "14px",
                          marginBottom: "8px",
                          paddingLeft: "20px",
                          position: "relative",
                        }}
                      >
                        <span
                          style={{
                            position: "absolute",
                            left: 0,
                            top: "8px",
                            width: "4px",
                            height: "4px",
                            backgroundColor: "#374151",
                            borderRadius: "50%",
                          }}
                        ></span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </Card>
              </Col>
            </Row>
          </div>

          {/* Continue Button Section */}
          <div style={{ borderTop: "1px solid #e5e7eb" }}>
            <div className="px-6 py-4 flex justify-end">
              <Button
                type="primary"
                size="large"
                disabled={!isValid}
                onClick={handleContinue}
                style={{
                  backgroundColor: isValid ? COLORS.primary : undefined,
                  fontWeight: 500,
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                Continue
                <ArrowRightOutlined />
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default TenderJobTypes;
